import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';
import {
  Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle,
  ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  useIsMobile,
} from '@erp/ui';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePurchaseDocuments } from '@/hooks/use-purchases';

type Range = '7d' | '30d' | '90d';

function rangeDays(r: Range): number {
  if (r === '7d') return 7;
  if (r === '30d') return 30;
  return 90;
}

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function ChartAreaInteractive() {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [range, setRange] = useState<Range>('90d');

  useEffect(() => {
    if (isMobile) setRange('7d');
  }, [isMobile]);

  const { data: invoices } = useSalesDocuments({ docType: 'invoice' });
  const { data: purchaseInvoices } = usePurchaseDocuments({ docType: 'invoice' });

  const data = useMemo(() => {
    const days = rangeDays(range);
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    start.setDate(end.getDate() - (days - 1));

    const points: Array<{ date: string; revenue: number; expenses: number }> = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const key = isoDay(d);
      const rev = (invoices ?? []).filter((x) => x.docDate === key).reduce((s, x) => s + x.totalTtc, 0);
      const exp = (purchaseInvoices ?? []).filter((x) => x.docDate === key).reduce((s, x) => s + x.totalTtc, 0);
      points.push({ date: key, revenue: rev, expenses: exp });
    }
    return points;
  }, [invoices, purchaseInvoices, range]);

  const chartConfig = {
    revenue: { label: t('dashboard.revenue'), color: 'var(--primary)' },
    expenses: { label: t('dashboard.expenses'), color: 'var(--chart-2)' },
  } satisfies ChartConfig;

  const totalRev = data.reduce((s, x) => s + x.revenue, 0);
  const totalExp = data.reduce((s, x) => s + x.expenses, 0);

  const fmt = (v: number) => v.toLocaleString(i18n.language === 'ar' ? 'ar-TN' : 'fr-TN', { maximumFractionDigits: 0 });

  const periodLabel =
    range === '7d' ? t('dashboard.last7Days')
      : range === '30d' ? t('dashboard.last30Days')
        : t('dashboard.last90Days');

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle>{t('dashboard.cashflow')}</CardTitle>
        <CardDescription>
          <span className="hidden @[540px]/card:inline">
            {t('dashboard.cashflowSummary', { revenue: fmt(totalRev), expenses: fmt(totalExp) })}
          </span>
          <span className="@[540px]/card:hidden">{periodLabel}</span>
        </CardDescription>
        <CardAction>
          <Select value={range} onValueChange={(v) => setRange(v as Range)}>
            <SelectTrigger className="w-36" size="sm" aria-label={t('dashboard.selectPeriod')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="90d" className="rounded-lg">{t('dashboard.last90Days')}</SelectItem>
              <SelectItem value="30d" className="rounded-lg">{t('dashboard.last30Days')}</SelectItem>
              <SelectItem value="7d" className="rounded-lg">{t('dashboard.last7Days')}</SelectItem>
            </SelectContent>
          </Select>
        </CardAction>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <AreaChart data={data}>
            <defs>
              <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={1.0} />
                <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="fillExpenses" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-expenses)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-expenses)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value: string) => {
                const d = new Date(value);
                return d.toLocaleDateString(i18n.language === 'ar' ? 'ar-TN' : 'fr-TN', { month: 'short', day: 'numeric' });
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) =>
                    new Date(value as string).toLocaleDateString(i18n.language === 'ar' ? 'ar-TN' : 'fr-TN', { month: 'short', day: 'numeric' })
                  }
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="expenses"
              type="natural"
              fill="url(#fillExpenses)"
              stroke="var(--color-expenses)"
              stackId="a"
            />
            <Area
              dataKey="revenue"
              type="natural"
              fill="url(#fillRevenue)"
              stroke="var(--color-revenue)"
              stackId="a"
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
